import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const VARIABLES = [
  { key: '{nombre}',     label: 'Nombre',      chipClass: 'var-chip-nombre' },
  { key: '{fecha}',      label: 'Fecha',        chipClass: 'var-chip-fecha'  },
  { key: '{hora}',       label: 'Hora',         chipClass: 'var-chip-hora'   },
  // Turnos ANSES: el evento está a la hora de la citación al estudio, no a la del organismo.
  { key: '{horaCitacion}',   label: 'Hora citación estudio', chipClass: 'var-chip-hora' },
  { key: '{horaTurnoAnses}', label: 'Hora turno ANSES',      chipClass: 'var-chip-hora' },
  { key: '{lugar}',      label: 'Lugar',        chipClass: 'var-chip-lugar'  },
  { key: '{tipoEvento}', label: 'Tipo Evento',  chipClass: 'var-chip-tipo'   },
];

const TEMPLATE_CONFIGS = [
  { key: 'turnoMedico',    label: 'Turno Médico',    icon: 'bi-bandaid-fill',         color: '#3b82f6', bg: '#eff6ff' },
  { key: 'turnoAnses',     label: 'Turno ANSES',     icon: 'bi-building-fill',         color: '#8b5cf6', bg: '#f5f3ff' },
  { key: 'juntaMedica',    label: 'Junta Médica',    icon: 'bi-people-fill',           color: '#0ea5e9', bg: '#f0f9ff' },
  { key: 'periciaMediaca', label: 'Pericia Médica',  icon: 'bi-clipboard2-pulse-fill', color: '#f59e0b', bg: '#fffbeb' },
  { key: 'default',        label: 'Otros',           icon: 'bi-calendar-check-fill',   color: '#64748b', bg: '#f8fafc' },
];

const DEFAULT_TEMPLATES = {
  turnoMedico:    'Hola {nombre}, te recordamos tu turno médico el {fecha} a las {hora} en {lugar}. Ante cualquier consulta, comunicate con nosotros.',
  turnoAnses:     'Hola {nombre}, te recordamos que tenés turno en ANSES el {fecha} a las {horaTurnoAnses} en {lugar}. Te esperamos en el estudio a las {horaCitacion}.',
  juntaMedica:    'Hola {nombre}, tu junta médica está programada para el {fecha} a las {hora} en {lugar}.',
  periciaMediaca: 'Hola {nombre}, tu pericia médica es el {fecha} a las {hora} en {lugar}.',
  default:        'Hola {nombre}, te recordamos tu {tipoEvento} el {fecha} a las {hora} en {lugar}.',
};

const INITIAL = {
  startHour:   '08:00',
  endHour:     '16:00',
  hoursBefore: 24,
  delayMin:    3,
  delayMax:    5,
  templates:   { ...DEFAULT_TEMPLATES },
};

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function renderPreview(text) {
  if (!text || !text.trim()) {
    return '<em style="color:#94a3b8">Sin mensaje configurado</em>';
  }
  return escapeHtml(text)
    .replace(/\{nombre\}/g,     '<mark class="var-hl var-hl-nombre">Juan García</mark>')
    .replace(/\{fecha\}/g,      '<mark class="var-hl var-hl-fecha">12/05/2026</mark>')
    .replace(/\{hora\}/g,       '<mark class="var-hl var-hl-hora">14:30</mark>')
    .replace(/\{lugar\}/g,      '<mark class="var-hl var-hl-lugar">Córdoba Capital</mark>')
    .replace(/\{tipoEvento\}/g, '<mark class="var-hl var-hl-tipo">Turno Médico</mark>');
}

function autoResize(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
}

function WhatsAppConfigForm({ config, loading, saving, onSave }) {
  const [form, setForm] = useState(INITIAL);
  const [savedRecently, setSavedRecently] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const textareaRefs = useRef({});
  const prevSaving = useRef(false);

  useEffect(() => {
    if (config) {
      setForm({
        ...INITIAL,
        ...config,
        delayMin:  Math.round((config.delayMin  ?? 3000) / 1000),
        delayMax:  Math.round((config.delayMax  ?? 5000) / 1000),
        templates: { ...DEFAULT_TEMPLATES, ...(config.templates || {}) },
      });
    }
  }, [config]);

  // Auto-resize todas las textareas cuando cambia el contenido
  useEffect(() => {
    Object.values(textareaRefs.current).forEach(autoResize);
  }, [form.templates]);

  // Indicador "guardado" al terminar de guardar
  useEffect(() => {
    if (prevSaving.current && !saving) {
      setSavedRecently(true);
      const t = setTimeout(() => setSavedRecently(false), 3000);
      return () => clearTimeout(t);
    }
    prevSaving.current = saving;
  }, [saving]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: ['hoursBefore', 'delayMin', 'delayMax'].includes(name) ? Number(value) : value,
    }));
  };

  const onTemplateChange = useCallback((key, value) => {
    setForm((prev) => ({
      ...prev,
      templates: { ...prev.templates, [key]: value },
    }));
  }, []);

  const resetTemplate = useCallback((key) => {
    setForm((prev) => ({
      ...prev,
      templates: { ...prev.templates, [key]: DEFAULT_TEMPLATES[key] },
    }));
  }, []);

  const insertarVariable = useCallback((variable) => {
    if (!activeKey) return;
    const el = textareaRefs.current[activeKey];
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const nuevo = el.value.substring(0, start) + variable + el.value.substring(end);
    onTemplateChange(activeKey, nuevo);
    setTimeout(() => {
      const ref = textareaRefs.current[activeKey];
      if (ref) {
        ref.selectionStart = start + variable.length;
        ref.selectionEnd   = start + variable.length;
        ref.focus();
        autoResize(ref);
      }
    }, 0);
  }, [activeKey, onTemplateChange]);

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      delayMin: form.delayMin * 1000,
      delayMax: form.delayMax * 1000,
    });
  };

  const activeLabel = TEMPLATE_CONFIGS.find((t) => t.key === activeKey)?.label;

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>

      {/* ── Configuracion de envios ── */}
      <section className="wa-admin-card">
        <header className="wa-admin-card-header">
          <h3 className="wa-admin-card-title">Configuracion de envios</h3>
        </header>
        <div className="wa-admin-card-body">
          {loading ? (
            <div className="wa-loader-inline">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
              Cargando configuracion...
            </div>
          ) : (
            <div className="wa-form-grid">
              <label>
                Hora inicio
                <input type="time" name="startHour" value={form.startHour} onChange={onChange} className="form-control" required />
              </label>
              <label>
                Hora fin
                <input type="time" name="endHour" value={form.endHour} onChange={onChange} className="form-control" required />
              </label>
              <label>
                Horas antes del turno
                <input type="number" min="0" name="hoursBefore" value={form.hoursBefore} onChange={onChange} className="form-control" required />
              </label>
              <label>
                Delay minimo (seg)
                <input type="number" min="0" step="1" name="delayMin" value={form.delayMin} onChange={onChange} className="form-control" required />
              </label>
              <label>
                Delay maximo (seg)
                <input type="number" min="0" step="1" name="delayMax" value={form.delayMax} onChange={onChange} className="form-control" required />
              </label>
            </div>
          )}
        </div>
      </section>

      {/* ── Mensajes predeterminados ── */}
      <section className="wa-admin-card wa-admin-card-full">
        <header className="wa-admin-card-header tmpl-section-header">
          <h3 className="wa-admin-card-title">Mensajes predeterminados de recordatorio</h3>
          <div className="tmpl-vars-bar">
            <span className="tmpl-vars-label">Variables disponibles</span>
            {VARIABLES.map(({ key, label, chipClass }) => (
              <button
                key={key}
                type="button"
                className={`tmpl-var-chip ${chipClass}`}
                onClick={() => insertarVariable(key)}
                title={activeKey
                  ? `Insertar en "${activeLabel}"`
                  : 'Hacé click en un mensaje primero'}
              >
                {label}
              </button>
            ))}
          </div>
        </header>

        <div className="wa-admin-card-body">
          {!activeKey && (
            <p className="tmpl-hint">
              <i className="bi bi-cursor-text me-1" />
              Hacé click en cualquier mensaje para editarlo, luego usá los chips de arriba para insertar variables.
            </p>
          )}

          <div className="tmpl-cards-grid">
            {TEMPLATE_CONFIGS.map(({ key, label, icon, color, bg }) => (
              <div
                key={key}
                className={`tmpl-card${activeKey === key ? ' tmpl-card-active' : ''}`}
              >
                {/* Header de la card */}
                <div className="tmpl-card-head">
                  <div className="tmpl-icon-wrap" style={{ background: bg, color }}>
                    <i className={`bi ${icon}`} />
                  </div>
                  <span className="tmpl-card-label">{label}</span>
                  <button
                    type="button"
                    className="tmpl-reset-btn"
                    onClick={() => resetTemplate(key)}
                    title="Restablecer plantilla por defecto"
                  >
                    <i className="bi bi-arrow-counterclockwise" />
                    Restablecer
                  </button>
                </div>

                {/* Textarea */}
                <textarea
                  ref={(el) => { textareaRefs.current[key] = el; }}
                  className="tmpl-textarea"
                  value={form.templates[key] || ''}
                  onChange={(e) => {
                    onTemplateChange(key, e.target.value);
                    autoResize(e.target);
                  }}
                  onFocus={() => setActiveKey(key)}
                  placeholder={DEFAULT_TEMPLATES[key]}
                  rows={3}
                  required
                />

                {/* Vista previa */}
                <div className="tmpl-preview-box">
                  <span className="tmpl-preview-label">
                    <i className="bi bi-eye me-1" />
                    Vista previa
                  </span>
                  <p
                    className="tmpl-preview-text"
                    dangerouslySetInnerHTML={{ __html: renderPreview(form.templates[key]) }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Footer con acciones */}
          <div className="tmpl-form-footer">
            {savedRecently && (
              <span className="tmpl-saved-badge">
                <i className="bi bi-check-circle-fill me-1" />
                Guardado correctamente
              </span>
            )}
            <button
              type="submit"
              className="btn btn-primary px-4"
              disabled={saving || loading}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="bi bi-floppy2-fill me-2" />
                  Guardar cambios
                </>
              )}
            </button>
          </div>
        </div>
      </section>
    </form>
  );
}

WhatsAppConfigForm.propTypes = {
  config: PropTypes.shape({
    startHour:   PropTypes.string,
    endHour:     PropTypes.string,
    hoursBefore: PropTypes.number,
    delayMin:    PropTypes.number,
    delayMax:    PropTypes.number,
    templates:   PropTypes.object,
  }),
  loading: PropTypes.bool,
  saving:  PropTypes.bool,
  onSave:  PropTypes.func.isRequired,
};

WhatsAppConfigForm.defaultProps = {
  config:  null,
  loading: false,
  saving:  false,
};

export default WhatsAppConfigForm;
